import { supabase } from './supabase';

/**
 * «Дай пропуск, которым правда можно ходить прямо сейчас».
 *
 * 🪤 Половина запросов приложения читала пропуск из зеркала в браузере
 * (localStorage['token']). Зеркало пишет отдельный крючок при входе, и оно
 * отстаёт от настоящего входа: в мини-приложении Телеграма человек входит по
 * ссылке из бота, вход проходит успешно, а зеркало остаётся пустым — вкладка
 * не успела его записать, либо запись в хранилище не удалась вовсе. Тогда
 * заголовок уходит ПУСТЫМ, сервер отвечает «не авторизован», и человек видит
 * ошибку сразу после успешного входа. Ровно это и случилось с голосовой
 * записью 23.08.2026: вход прошёл, а запись не отправилась.
 *
 * Второй случай того же рода — пропуск просто устарел: он живёт около часа, а
 * зеркало никто не обновляет.
 *
 * Здесь пропуск берётся у самой библиотеки входа: она знает живую сессию и
 * умеет продлить её сама. Зеркало обновляем попутно — ради старого кода,
 * который читает его напрямую.
 */

// Продлеваем заранее, пока запас ещё есть: запрос, отправленный ровно на
// границе, иначе проигрывает часам.
const REFRESH_SKEW_SECONDS = 120;

let inFlight: Promise<string | null> | null = null;

async function resolve(force: boolean): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    let active = session;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = active?.expires_at ?? 0;
    const stale = force || !active?.access_token || expiresAt - now <= REFRESH_SKEW_SECONDS;

    if (stale) {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) active = data.session;
    }

    const token = active?.access_token ?? null;
    const live = !!token && (active?.expires_at ?? 0) - Math.floor(Date.now() / 1000) > 0;

    if (live) {
      // Зеркало обновляем только живым пропуском. Записать просроченный —
      // хуже, чем не записать: старый код будет вечно посылать мёртвый.
      try { localStorage.setItem('token', token!); } catch { /* хранилище закрыто */ }
      return token;
    }
    return null;
  } catch {
    // Связь мигнула — отдаём то, что было в зеркале. Решать, что делать с
    // отказом, будет вызывающий.
    try { return localStorage.getItem('token'); } catch { return null; }
  }
}

/** Живой пропуск или null. Одновременные вызовы сводятся в один запрос. */
export function getFreshAccessToken(force = false): Promise<string | null> {
  if (!inFlight) {
    inFlight = resolve(force).finally(() => { inFlight = null; });
  }
  return inFlight;
}

/**
 * Готовый заголовок. Пустой объект, если пропуска нет — так сервер ответит
 * честным «не авторизован», а не примет запрос за чужой.
 */
export async function authHeader(
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const token = await getFreshAccessToken();
  const h: Record<string, string> = { ...(extra || {}) };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}
